<template>
    <Head :title="auto_accept ? t('profile.collaboration.acceptShared.pageTitleAutoAccept') : t('profile.collaboration.acceptShared.pageTitle')" />
    <BaseLayout gap="lg" fill-height content-class="justify-center text-ink">
        <Transition mode="out-in" name="fade">
            <LoadingStep
                v-if="isAccepting"
                :main-loading-message="
                    formattedName ? t('profile.collaboration.acceptShared.opening', { name: formattedName }) : t('profile.collaboration.acceptShared.openingAnonymous')
                "
                :hide-messages="true" />

            <AmbiguousMatchConfirm
                v-else-if="matchCandidates.length"
                :candidates="matchCandidates"
                :first-name="firstName"
                :error="error"
                :busy="isAccepting"
                :focus-on-mount="!auto_accept || resolution !== null"
                @select="resolveWith"
                @create-new="resolveWith(null)" />
            <BaseNotification v-else-if="auto_accept && error" theme="error" :text="error" hide-dismiss />
            <div v-else class="flex flex-col gap-6 rounded-2xl bg-surface-raised border border-border shadow-sm p-6 md:p-10">
                <BasePageTitle :title="title">
                    <template #subtitle>
                        {{ subtitle }}
                    </template>
                </BasePageTitle>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
                <BaseButton theme="primary" class="self-center" @click="handleAccept">{{ t("profile.collaboration.acceptShared.accept") }}</BaseButton>
            </div>
        </Transition>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import { capitalizeName, getFirstName } from "~vue/utils/stringUtils";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ConnectionMatchCandidate, ProfileShareAttribution, ShareInvitationAccepted, ShareInvitationAcceptRequest } from "~vue/api/generated/collab-profiles";
import AmbiguousMatchConfirm from "~vue/CollaborationProfile/AmbiguousMatchConfirm.vue";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { canComparePerspectives } = useProfileSharingCopy();

const props = defineProps<{
    invitation_id: string;
    owner_name: string;
    target_user_uuid: string;
    profile_url: string;
    share_attribution?: ProfileShareAttribution | null;
    auto_accept?: boolean;
}>();

const isAccepting = ref<boolean>(!!props.auto_accept);
const error = ref<string | null>(null);
const matchCandidates = ref<ConnectionMatchCandidate[]>([]);
// "new" while the create-a-card resolution is in flight, a UUID while Select is.
const resolution = ref<string | null>(null);

const formattedName = computed(() => (props.owner_name ? capitalizeName(getFirstName(props.owner_name)) : ""));
const firstName = formattedName;

const title = computed(() =>
    formattedName.value ? t("profile.collaboration.acceptShared.title", { name: formattedName.value }) : t("profile.collaboration.acceptShared.titleAnonymous"),
);

const subtitle = computed(() => {
    if (canComparePerspectives.value) {
        return formattedName.value
            ? t("profile.collaboration.acceptShared.subtitleCompare", { name: formattedName.value })
            : t("profile.collaboration.acceptShared.subtitleCompareAnonymous");
    }
    return formattedName.value ? t("profile.collaboration.acceptShared.subtitle", { name: formattedName.value }) : t("profile.collaboration.acceptShared.subtitleAnonymous");
});

const acceptPayload = computed<ShareInvitationAcceptRequest>(() => {
    if (resolution.value === "new") {
        return { share_attribution: props.share_attribution ?? null, create_new: true };
    }
    if (resolution.value !== null) {
        return { share_attribution: props.share_attribution ?? null, connection_id: resolution.value };
    }
    return { share_attribution: props.share_attribution ?? null };
});
const {
    error: acceptError,
    data: acceptData,
    response: acceptResponse,
    execute: executeAccept,
} = useFetch(`/v1/shared-profile-invitations/${props.invitation_id}/accept`, {
    immediate: false,
})
    .post(acceptPayload)
    .json<ShareInvitationAccepted>();

const handleAccept = async (): Promise<void> => {
    isAccepting.value = true;
    error.value = null;
    await executeAccept();
    if (acceptError.value) {
        error.value = t("profile.collaboration.acceptShared.invalidInvitation");
        isAccepting.value = false;
        return;
    }
    const candidates = acceptData.value?.match_candidates ?? [];
    if (candidates.length) {
        matchCandidates.value = candidates;
        isAccepting.value = false;
        return;
    }
    // Replace history so the back button doesn't return to the confirm page
    router.visit(props.profile_url, { replace: true });
};

const resolveWith = async (connectionId: string | null): Promise<void> => {
    // A second tap during the exit transition must not send a conflicting accept.
    if (isAccepting.value) {
        return;
    }
    resolution.value = connectionId ?? "new";
    error.value = null;
    isAccepting.value = true;
    await executeAccept();
    if (acceptError.value) {
        // 409 marks a stale pick: the selected card was linked elsewhere or
        // dismissed — every other failure is the generic retry.
        error.value = acceptResponse.value?.status === 409 ? t("profile.collaboration.acceptShared.cardNoLongerAvailable") : t("profile.collaboration.acceptShared.resolveError");
        isAccepting.value = false;
        return;
    }
    router.visit(props.profile_url, { replace: true });
};

onMounted(() => {
    if (props.auto_accept) {
        void handleAccept();
    }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
